import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

function Section({ number, title, children }) {
  return (
    <div className="flex space-x-4">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-vextaris-lime/10 border border-vextaris-lime/30 flex items-center justify-center text-vextaris-lime text-sm font-medium">
        {number}
      </div>
      <div className="flex-1 pb-8">
        <h3 className="text-white font-medium mb-2">{title}</h3>
        <div className="text-gray-400 text-sm leading-relaxed space-y-2">{children}</div>
      </div>
    </div>
  );
}

export default function DeployGuide() {
  return (
    <div className="max-w-3xl mx-auto">
      <Link to="/guide" className="inline-flex items-center text-gray-500 hover:text-white text-sm mb-6 transition-colors">
        <ArrowLeftIcon className="w-4 h-4 mr-2" />
        Back to Guide
      </Link>
      <h1 className="text-3xl font-bold text-white mb-8">Using the Deploy Suite</h1>

      <p className="text-gray-400 leading-relaxed mb-10">
        The Deploy Suite provides three interfaces for deploying on-chain assets: visual templates
        for common operations, a terminal for direct CLI commands, and a JSON editor for programmatic
        configuration. All three produce identical on-chain results — choose the one that fits your workflow.
      </p>

      <div className="space-y-2">
        <Section number={1} title="Navigate to Deploy">
          <p>
            Open the <span className="text-white">Deploy</span> page from the sidebar. You will see three
            tabs: <span className="text-white">Templates</span>, <span className="text-white">Terminal</span>,
            and <span className="text-white">Editor</span>.
          </p>
        </Section>

        <Section number={2} title="Templates — visual deployment">
          <p>
            The Templates tab offers five pre-built deployment flows:
          </p>
          <ul className="list-disc list-inside space-y-1 mt-1">
            <li><span className="text-white">Token</span> — Create a VSC-7 fungible token with metadata</li>
            <li><span className="text-white">NFT Collection</span> — Deploy a VSC-21 NFT collection with royalties</li>
            <li><span className="text-white">Multi-Token</span> — Create a VSC-55 multi-token collection</li>
            <li><span className="text-white">Presale</span> — Launch a VSC-LAUNCH presale with escrow</li>
            <li><span className="text-white">DEX Pool</span> — Create an AMM liquidity pool on VexiDEX</li>
          </ul>
          <p>
            Select a template, fill out the form fields, and review the CLI-equivalent command shown
            below the form. This lets you verify exactly what will be submitted before signing.
          </p>
        </Section>

        <Section number={3} title="Review and customize">
          <p>
            Each template shows the generated CLI command and the raw transaction payload. You can adjust
            values directly in the form — the preview updates in real time. When satisfied, proceed to
            wallet connection and submission.
          </p>
        </Section>

        <Section number={4} title="Terminal — direct commands">
          <p>
            The Terminal tab provides a command-line interface. Type <code className="text-vextaris-blue">help</code> to
            see the full list of available commands. This gives you access to every operation the CLI
            supports, including operations not covered by templates.
          </p>
          <p>
            Commands follow the same syntax as
            the <code className="text-vextaris-blue">vexidus</code> CLI tool. If you are familiar with
            the CLI, the terminal is the fastest path.
          </p>
        </Section>

        <Section number={5} title="Editor — JSON configuration">
          <p>
            The Editor tab accepts raw JSON transaction payloads. Paste a configuration object and the
            editor will validate the structure, highlight errors, and let you submit directly.
          </p>
          <p>
            This is useful for automation workflows where you generate deployment configs programmatically
            and want to submit them through the web UI rather than the CLI.
          </p>
        </Section>

        <Section number={6} title="Connect wallet and deploy">
          <p>
            Regardless of which tab you use, the final step is the same: connect your wallet, review
            the transaction summary, and sign. Deployment is confirmed within a single block (typically
            under 2 seconds).
          </p>
        </Section>
      </div>

      {/* Tips */}
      <div className="mt-6 bg-vextaris-darker border border-gray-800 rounded-xl p-6">
        <h3 className="text-white font-medium mb-3">Tips</h3>
        <ul className="text-gray-400 text-sm space-y-2 list-disc list-inside">
          <li><span className="text-white">Templates</span> are fastest for standard deployments — use them when a pre-built flow exists.</li>
          <li><span className="text-white">Terminal</span> gives full CLI access for advanced operations and edge cases.</li>
          <li><span className="text-white">Editor</span> is best for automation, scripting, and reproducible deployments.</li>
          <li>All three interfaces produce the same on-chain transaction. There is no difference in outcome.</li>
          <li>Review the CLI-equivalent output in Templates to learn the command syntax for future terminal use.</li>
        </ul>
      </div>

      {/* Docs link */}
      <div className="mt-6 text-sm text-gray-500">
        For the full CLI command reference, see{' '}
        <a href="https://docs.vextaris.io/developers/cli" target="_blank" rel="noopener noreferrer" className="text-vextaris-blue hover:underline">
          docs.vextaris.io/developers/cli
        </a>.
      </div>
    </div>
  );
}
